import { useState, useCallback, useRef } from 'react';

const GIS_SDK_URL = 'https://accounts.google.com/gsi/client';
const PICKER_API_BASE = '/api/v1/google-photos';
const SCOPE = 'https://www.googleapis.com/auth/photospicker.mediaitems.readonly';
const POLL_INTERVAL_MS = 5000;
const MAX_POLL_ATTEMPTS = 180; // 15 minutes: long enough to browse a big library
/** How many photographs are downloaded at once. See the pool in `importPhotos`. */
const MAX_CONCURRENT_DOWNLOADS = 4;

export type GooglePhotosStatus =
  | 'idle'
  | 'loading-sdk'
  | 'authorizing'
  | 'picking'
  | 'importing'
  | 'done'
  | 'error';

function getClientId(): string {
  const id = import.meta.env.VITE_GOOGLE_CLIENT_ID;
  if (!id) throw new Error('VITE_GOOGLE_CLIENT_ID is not set');
  return id;
}

function loadGisSdk(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.google?.accounts?.oauth2) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = GIS_SDK_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google Identity Services SDK'));
    document.head.appendChild(script);
  });
}

function requestAccessToken(clientId: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const client = window.google!.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope: SCOPE,
      callback: (response) => {
        if (response.error) {
          reject(new Error(response.error_description || response.error));
        } else {
          resolve(response.access_token);
        }
      },
      error_callback: (error) => {
        reject(new Error(error.message || 'OAuth authorization failed'));
      },
    });
    client.requestAccessToken();
  });
}

interface PickerSession {
  id: string;
  pickerUri: string;
  mediaItemsSet: boolean;
}

async function createPickerSession(token: string): Promise<PickerSession> {
  const res = await fetch(`${PICKER_API_BASE}/sessions`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: '{}',
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Failed to create picker session: ${res.status} ${text}`);
  }
  return res.json();
}

async function getPickerSession(token: string, sessionId: string): Promise<PickerSession> {
  const res = await fetch(`${PICKER_API_BASE}/sessions/${sessionId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Failed to get picker session: ${res.status} ${text}`);
  }
  return res.json();
}

function waitForPick(
  token: string,
  sessionId: string,
  signal: AbortSignal,
): Promise<PickerSession> {
  return new Promise((resolve, reject) => {
    let attempts = 0;

    const check = async () => {
      if (signal.aborted) {
        reject(new Error('Cancelled'));
        return;
      }
      attempts++;
      if (attempts > MAX_POLL_ATTEMPTS) {
        reject(new Error('Picker session timed out'));
        return;
      }
      try {
        const session = await getPickerSession(token, sessionId);
        if (session.mediaItemsSet) {
          resolve(session);
        } else {
          setTimeout(check, POLL_INTERVAL_MS);
        }
      } catch (err) {
        reject(err);
      }
    };

    setTimeout(check, POLL_INTERVAL_MS);
  });
}

interface MediaItem {
  id: string;
  type: string;
  mediaFile?: {
    baseUrl: string;
    mimeType: string;
    filename: string;
  };
}

async function fetchMediaItems(token: string, sessionId: string): Promise<MediaItem[]> {
  const items: MediaItem[] = [];
  let pageToken: string | undefined;

  do {
    const params = new URLSearchParams({ sessionId, pageSize: '100' });
    if (pageToken) params.set('pageToken', pageToken);

    const res = await fetch(`${PICKER_API_BASE}/mediaItems?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`Failed to fetch media items: ${res.status} ${text}`);
    }
    const data = await res.json();
    if (data.mediaItems) items.push(...data.mediaItems);
    pageToken = data.nextPageToken;
  } while (pageToken);

  return items;
}

// Marker type for files that were already uploaded to S3 by the backend.
// The upload queue detects this and skips the presigned-URL upload step.
export type S3PreImportedFile = File & {
  _s3Import: { s3Key: string; fileSizeBytes: number };
};

async function downloadAsFile(
  baseUrl: string,
  fileName: string,
  mimeType: string,
  token: string,
): Promise<File> {
  const isVideo = mimeType.startsWith('video/');

  if (isVideo) {
    // Videos can exceed the 6MB API Gateway response limit, so the backend
    // streams them directly from Google Photos to S3 and returns the S3 key.
    const importAbort = new AbortController();
    const importTimeout = setTimeout(() => importAbort.abort(), 120_000);
    let res: Response;
    try {
      res = await fetch(`${PICKER_API_BASE}/import-media`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ baseUrl, fileName, mimeType }),
        signal: importAbort.signal,
      });
    } finally {
      clearTimeout(importTimeout);
    }
    if (!res.ok) throw new Error(`Failed to import video: ${res.status}`);
    const { s3Key, fileSizeBytes } = (await res.json()) as {
      s3Key: string;
      fileSizeBytes: number;
    };
    // Return an empty placeholder File with the S3 import metadata attached.
    // The upload queue will detect _s3Import and skip the upload step.
    const placeholder = new File([], fileName, { type: mimeType }) as S3PreImportedFile;
    placeholder._s3Import = { s3Key, fileSizeBytes };
    return placeholder;
  }

  // Images: proxy through the backend content endpoint to avoid CORS.
  const suffixes = ['=w2048-h2048'];
  for (const suffix of suffixes) {
    const downloadUrl = `${baseUrl}${suffix}`.replace(
      /^https:\/\/[a-z0-9-]+\.googleusercontent\.com/,
      `${PICKER_API_BASE}/content`,
    );
    try {
      const res = await fetch(downloadUrl, {
        headers: { Authorization: `Bearer ${token}` },
        redirect: 'follow',
      });
      if (res.ok) {
        const blob = await res.blob();
        return new File([blob], fileName, { type: mimeType || blob.type });
      }
    } catch {
      // Try next suffix
    }
  }
  throw new Error(`Failed to download ${fileName}`);
}

export function useGooglePhotos() {
  const [status, setStatus] = useState<GooglePhotosStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const pickerWindowRef = useRef<Window | null>(null);
  /**
   * The Google picker's own URL, published while we wait on it.
   *
   * ⚠️ **The window we open is not a reliable way in.** A popup blocker can
   * swallow it, Google's picker app sometimes hangs on its own spinner, and a
   * visitor can simply close it — and in every one of those cases the session
   * is alive and we poll it for fifteen minutes while the person stares at a
   * button that says "Picking…". Handing the URL back gives them a second door
   * into the *same* session, so a failed window costs a click rather than the
   * whole import.
   */
  const [pickerUrl, setPickerUrl] = useState<string | null>(null);

  const importPhotos = useCallback(
    async (onFileReady?: (file: File) => void | Promise<void>): Promise<File[]> => {
      // Abort any previous import
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setError(null);
      const files: File[] = [];

      try {
        const clientId = getClientId();

        // Step 1: Load GIS SDK
        setStatus('loading-sdk');
        await loadGisSdk();

        // Step 2: Get OAuth token
        setStatus('authorizing');
        const token = await requestAccessToken(clientId);

        if (controller.signal.aborted) throw new Error('Cancelled');

        // Step 3: Create picker session and open picker
        setStatus('picking');
        const session = await createPickerSession(token);
        setPickerUrl(session.pickerUri);
        // A plain tab, not a sized popup. The picker is a full Google web app
        // and a stripped popup is the context it copes with worst; a tab also
        // survives a blocker that would kill a popup outright.
        pickerWindowRef.current = window.open(session.pickerUri, '_blank');

        // Step 4: Poll until user finishes picking
        await waitForPick(token, session.id, controller.signal);

        // Close the picker window now that selection is done
        try {
          pickerWindowRef.current?.close();
        } catch {
          /* cross-origin */
        }
        pickerWindowRef.current = null;
        setPickerUrl(null);

        if (controller.signal.aborted) throw new Error('Cancelled');

        // Step 5: Fetch media items
        setStatus('importing');
        const items = await fetchMediaItems(token, session.id);

        // Step 6: Download a few at a time, handing each to the caller as it lands.
        //
        // ⚠️ **A few at a time, never all of them.** This used to map every picked
        // item through `Promise.allSettled`, which opens one download per photo at
        // once. A picker session of two thousand photographs then held two
        // thousand images in memory before a single one had been saved, and the
        // tab died with the lot — which is precisely what an import of that size
        // did. The pool bounds what is in flight, and awaiting `onFileReady` lets
        // the caller hold the line while its own queue drains, so memory stays
        // flat whether the selection is ten photographs or ten thousand.
        const pending = items.filter((item) => !!item.mediaFile?.baseUrl);
        let next = 0;

        const worker = async () => {
          for (let i = next++; i < pending.length; i = next++) {
            if (controller.signal.aborted) return;
            const item = pending[i];
            const mediaFile = item.mediaFile!;
            // Skip videos still being processed by Google
            const isVideo = mediaFile.mimeType?.startsWith('video/');
            const processingStatus =
              (mediaFile as Record<string, unknown>)?.mediaFileMetadata &&
              ((mediaFile as any).mediaFileMetadata?.videoMetadata?.processingStatus as
                | string
                | undefined);
            if (isVideo && processingStatus && processingStatus !== 'READY') {
              console.warn(`Skipping ${mediaFile.filename}: video still ${processingStatus}`);
              continue;
            }
            try {
              const file = await downloadAsFile(
                mediaFile.baseUrl,
                mediaFile.filename || `google-photo-${item.id}.jpg`,
                mediaFile.mimeType || 'image/jpeg',
                token,
              );
              files.push(file);
              await onFileReady?.(file);
            } catch {
              console.warn(`Failed to download ${mediaFile.filename}`);
            }
          }
        };

        await Promise.all(
          Array.from({ length: Math.min(MAX_CONCURRENT_DOWNLOADS, pending.length) }, () =>
            worker(),
          ),
        );

        setStatus('done');
        return files;
      } catch (err) {
        if (controller.signal.aborted) {
          setStatus('idle');
          return [];
        }
        const message = err instanceof Error ? err.message : 'Google Photos import failed';
        setError(message);
        setStatus('error');
        return [];
      }
    },
    [],
  );

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    try {
      pickerWindowRef.current?.close();
    } catch {
      /* cross-origin */
    }
    pickerWindowRef.current = null;
    setPickerUrl(null);
    setStatus('idle');
    setError(null);
  }, []);

  return { importPhotos, cancel, status, error, pickerUrl };
}
